/* eslint no-underscore-dangle: 0 */
/* eslint import/no-extraneous-dependencies: 0 */
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { toPng } from 'html-to-image';
import { setErrorNotification } from '../../../../redux/slices/NotificationSlice';
import ModalBox from '../../../../shared/component/ModalBox';
import TemplateContentUpload from '../TemplateContentUpload';
import HtmlHeader from '../HtmlHeader';
import utils from '../templateFunctions';
import FileFormat from '../FileFormat';
import { createOrUpdateTemplate } from '../../../../redux/slices/TemplateSlice';
import PopupBox from '../ContentUploadConfirmation';
import roundeBig from '../../../../assets/images/Template_29/roundex-big.png';
import roundeSamll from '../../../../assets/images/Template_29/rounged-small.png';

function HtmlTemplateTwentynine({ templateData }) {
  const [data, setData] = useState([]);
  const [notification, setNotification] = useState(false);
  const [openContentUploadConfirmation, setOpenContentUploadConfirmation] = useState(false);
  const [base64, setBase64] = useState('');
  const [htmlContent, setHtmlContent] = useState('');
  const [disable, setDisable] = useState(true);
  const [modalView, setModalView] = useState({ content: '', title: '' });
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const htmlRef = useRef(null);

  const cssStyles = `
    body {
      margin: 0;
      padding: 0;
    }
  .flash-card {
      position: relative;
      font-family: "Montserrat", sans-serif;
      box-sizing: border-box;
      overflow: hidden;
      height: 93.6vh;
      background-image: url(../../../assets/images/Template_29/background.png);
      background-size: cover;
      background-position: center;
      z-index: 1;
    }
    .flash-card__image-top {
      position: absolute;
      width: 100px;
      height: 89px;
      top: 0px;
      inset-inline-end: 0;
    }
    .flash-card__image-bottom {
      position: absolute;
      width: 100px;
      height: 89px;
      bottom: 0;
      transform: rotate(180deg);
      inset-inline-start: 0;
    }
    .flash-card__header {
      width: 100%;
      display: flex;
      align-items: center;
      gap: 7px;
    }
    .flash-card__header h3 {
      color: #FFF;
    }
    .flash-card__header img {
      width: 20px;
    }
    .flash-card .main-container {
      height: 100%;
      display: flex;
      align-items: center;
      flex-direction: column;
      padding: 50px;
    }
    .flash-card .flash-content {
      display: flex;
      align-items: center;
      flex-direction: column;
      justify-content: center;
      gap: 20px;
      width: 100%;
      height: 100%;
      position: relative;
    }
    .flash-card .flash-content__image-top {
      position: absolute;
      width: 100px;
      height: 100px;
      top: 0;
      inset-inline-start: -20px;
    }
    .flash-card .flash-content__image-bottom {
      position: absolute;
      width: 100px;
      height: 100px;
      bottom: 20px;
      inset-inline-end: 30px;
    }
    .flash-card .flash-content__flash-bg {
      z-index: -1;
      position: absolute;
      width: 110%;
      height: 110%;
      background-size: contain;
      background-position: center;
      background-repeat: no-repeat;
      background-image: url(../../../assets/images/Template_29/mask.png);
    }
    .flash-card .flash-content__text-container {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 30px;
    }
    .flash-card .flash-content__desc {
      display: flex;
      gap: 5px;
      margin-block: 0;
      font-size: 80px;
      font-weight: 800;
      color: #246D98;
    }
    .flash-card .flash-content__title {
      font-weight: 900;
      font-size: 130px;
      color: #FFF;
      background-color: #002E4A;
      padding: 40px;
    }
    .flash-card .flash-content__offer {
      margin-block: 0;
      font-size: 80px;
      font-weight: 800;
      color: #246D98;
    }
`;

  const convertImage = async (url) => {
    await utils.convertSignedUrl(url)
      .then((response) => {
        setDisable(false);
        dispatch(createOrUpdateTemplate(response));
      })
      .catch((error) => {
        setDisable(false);
        dispatch(setErrorNotification(error?.response?.data));
      });
  };

  const convertBase64 = async () => {
    toPng(htmlRef?.current, { cacheBust: true })
      .then((dataUrl) => {
        setBase64(dataUrl);
        convertImage(dataUrl);
      })
      .catch((error) => {
        dispatch(setErrorNotification(error?.response?.data));
      });
  };

  const convertHtml = () => {
    if (htmlRef.current) {
      const content = htmlRef.current.outerHTML;
      const fullHtml = `
        <html>
          <head>
              <link rel="preconnect" href="https://fonts.googleapis.com" />
              <link rel="preconnect" href="https://fonts.gstatic.com" />
              <link 
              href="https://fonts.googleapis.com/css2?family=Great+Vibes&family=Urbanist:wght@100..900&display=swap" 
              rel="stylesheet" />
              <style>${cssStyles}</style>
          </head>
          <body>${content}</body>
        </html>
      `;
      setHtmlContent(fullHtml); // Set the HTML content as a string
    }
  };

  useEffect(() => {
    utils?.initializer(templateData).then((res) => (setData(res)));
    utils?.reload();
  }, [templateData]);

  /* useEffect runs convertBase64 after data is updated */
  useEffect(() => {
    if (data) {
      /* used settimeout the page was fully rendered and then called the api so wait for the response */
      setTimeout(() => {
        convertBase64();
      }, 1000);
      convertHtml();
    }
  }, [data]);

  const handleFileType = (fileType) => {
    setModalView({
      title: 'content',
      content: <TemplateContentUpload
        base64={base64}
        setNotification={setNotification}
        fileType={fileType}
        htmlContent={htmlContent}
      />,
    });
  };

  const onClickContentUpload = () => {
    setNotification(true);
    setModalView({
      title: t('fileFormat'),
      content: <FileFormat
        setNotification={setNotification}
        handleFileType={handleFileType}
        setOpenContentUploadConfirmation={setOpenContentUploadConfirmation}
        data={data}
      />,
    });
  };

  const contentDetails = data?.[0]?.template_contents?.items?.[0]?.details;
  const backgroundColor = data?.[0]?.styles?.background;
  const text = 'UP TO';
  const mainText = contentDetails?.[0]?.item_name ? contentDetails[0].item_name.toUpperCase() : '';

  const offerPercentage = contentDetails?.[0]?.offer_percentage
    ? contentDetails?.[0]?.offer_percentage?.toUpperCase() : '';
  const offerText = offerPercentage ? `${offerPercentage} OFF` : '';

  return (
    <div>
      <HtmlHeader
        htmlData={data}
        disable={disable}
        click={onClickContentUpload}
      />

      <ModalBox
        status={notification}
        closeModal={setNotification}
        modalView={modalView}
      />

      <ModalBox
        status={openContentUploadConfirmation}
        closeModal={setOpenContentUploadConfirmation}
        modalView={{
          title: 'Confirmation',
          content: <PopupBox
            setOpenContentUploadConfirmation={setOpenContentUploadConfirmation}
            setNotification={setNotification}
          />,
        }}
      />

      <div className="header-height" />

      <motion.div
        ref={htmlRef}
        className="flash-card"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: [0, 1.2, 0.9, 1], opacity: 1 }}
        transition={{ duration: 0.8, ease: 'easeOut' }}
      >
        <img
          className="flash-card__image-top"
          src={roundeBig}
          alt="Left Top"
        />
        <img
          className="flash-card__image-bottom"
          src={roundeBig}
          alt="Left Bottom"
        />

        <div style={{ backgroundColor: `${backgroundColor}` }} className="main-container">
          <div className="flash-card__header">
            <motion.h3
              initial={{ opacity: 0, scale: 0.3 }}
              animate={{ opacity: 1, scale: [0.3, 1.1, 1], y: [30, -10, 0] }}
              transition={{ duration: 0.4, ease: 'easeOut', delay: 0.8 }}
            >
              {contentDetails?.[0]?.header?.toUpperCase()}
            </motion.h3>
          </div>

          <motion.div
            className="flash-content"
            variants={{
              hidden: { opacity: 0 },
              show: {
                opacity: 1,
                transition: {
                  delayChildren: 0.6,
                  staggerChildren: 0.2,
                },
              },
            }}
          >

            <motion.div
              className="flash-content__flash-bg"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{
                duration: 0.6,
                delay: 0.4,
                ease: 'easeOut',
              }}
            />

            <motion.img
              className="flash-content__image-top"
              src={roundeSamll}
              alt="Left Top"
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: [0, 1, 0.9, 1],
                opacity: [0, 1, 1, 1],
              }}
              transition={{
                duration: 2.5,
                delay: 1.2,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />

            <motion.img
              className="flash-content__image-bottom"
              src={roundeSamll}
              alt="Left Bottom"
              initial={{ scale: 0, opacity: 0 }}
              animate={{
                scale: [0, 1, 0.9, 1],
                opacity: [0, 1, 1, 1],
              }}
              transition={{
                duration: 2.5,
                delay: 1.4,
                repeat: Infinity,
                ease: 'easeInOut',
              }}
            />

            <motion.div
              className="flash-content__text-container"
              initial="hidden"
              animate="show"
              variants={{
                hidden: { opacity: 0 },
                show: {
                  opacity: 1,
                  transition: {
                    delayChildren: 2.0,
                    staggerChildren: 0.1,
                  },
                },
              }}
            >
              <motion.p className="flash-content__desc">
                {text.split('').map((char, index) => (
                  <motion.span
                    key={char.id}
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{
                      duration: 0.3,
                      delay: 2.0 + (index * 0.05),
                      ease: 'easeOut',
                    }}
                    style={{ display: 'inline-block' }}
                  >
                    {char === ' ' ? '\u00A0' : char}
                  </motion.span>
                ))}
              </motion.p>

              <motion.h1
                className="flash-content__title"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{
                  duration: 0.6,
                  delay: 1,
                  ease: 'easeOut',
                }}
              >
                {mainText && mainText.split('').map((char, index) => (
                  <motion.span
                    key={char.id}
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{
                      duration: 0.3,
                      delay: 2.2 + (index * 0.05),
                      ease: 'easeOut',
                    }}
                    style={{ display: 'inline-block' }}
                  >
                    {char === ' ' ? '\u00A0' : char}
                  </motion.span>
                ))}
              </motion.h1>

              <motion.p className="flash-content__offer">
                {offerText.split('').map((char, index) => (
                  <motion.span
                    key={char.id}
                    initial={{ y: -50, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{
                      duration: 0.3,
                      delay: 2.4 + (index * 0.05),
                      ease: 'easeOut',
                    }}
                    style={{ display: 'inline-block' }}
                  >
                    {char === ' ' ? '\u00A0' : char}
                  </motion.span>
                ))}
              </motion.p>
            </motion.div>

          </motion.div>
        </div>
      </motion.div>
    </div>
  );
}

HtmlTemplateTwentynine.propTypes = {
  templateData:
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      template_title: PropTypes.string.isRequired,
    }).isRequired,
  // Ensure templateData is an array of objects with specific shape
};

export default HtmlTemplateTwentynine;
